import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ExternalLink, X } from 'lucide-react';
import type { DelegateCalendarEventRow } from '../../utils/executiveCalendarDelegatedService';

export type EventEditorDraft = {
  title: string;
  start: Date;
  end: Date;
  location: string;
  description: string;
};

type EventEditorPopoverProps = {
  event: DelegateCalendarEventRow;
  anchorRect: DOMRect;
  calendarName: string;
  calendarColor: string;
  isGrouped: boolean;
  onSave: (draft: EventEditorDraft) => Promise<void>;
  onDelete: () => Promise<void>;
  onClose: () => void;
};

const DURATION_CHIPS = [
  { label: '30m', minutes: 30 },
  { label: '1h', minutes: 60 },
  { label: '90m', minutes: 90 },
  { label: '2h', minutes: 120 },
] as const;

const POPOVER_WIDTH = 360;

function combine(dateStr: string, timeStr: string): Date | null {
  if (!dateStr || !timeStr) return null;
  const d = new Date(`${dateStr}T${timeStr}`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr${h > 1 ? 's' : ''}` : `${h} hr ${m} min`;
}

/** Rich event editor anchored beside the clicked pill (replaces the centered modal). */
const EventEditorPopover = ({
  event,
  anchorRect,
  calendarName,
  calendarColor,
  isGrouped,
  onSave,
  onDelete,
  onClose,
}: EventEditorPopoverProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; arrowSide: 'left' | 'right' } | null>(
    null,
  );

  const initial = (() => {
    try {
      const s = event.start ? parseISO(event.start) : null;
      const e = event.end ? parseISO(event.end) : null;
      return {
        date: s ? format(s, 'yyyy-MM-dd') : '',
        startTime: s ? format(s, 'HH:mm') : '',
        endTime: e ? format(e, 'HH:mm') : '',
      };
    } catch {
      return { date: '', startTime: '', endTime: '' };
    }
  })();

  const [title, setTitle] = useState(event.title);
  const [dateStr, setDateStr] = useState(initial.date);
  const [startTime, setStartTime] = useState(initial.startTime);
  const [endTime, setEndTime] = useState(initial.endTime);
  const [location, setLocation] = useState(event.location || '');
  const [description, setDescription] = useState(event.description || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const start = combine(dateStr, startTime);
  const end = combine(dateStr, endTime);
  const durationMin =
    start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : null;
  const timesValid = durationMin !== null && durationMin > 0;

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const { height } = card.getBoundingClientRect();
    const margin = 10;
    let left = anchorRect.right + margin;
    let arrowSide: 'left' | 'right' = 'left';
    if (left + POPOVER_WIDTH > window.innerWidth - margin) {
      left = anchorRect.left - POPOVER_WIDTH - margin;
      arrowSide = 'right';
    }
    left = Math.max(margin, left);
    let top = anchorRect.top - 8;
    if (top + height > window.innerHeight - margin) {
      top = window.innerHeight - height - margin;
    }
    top = Math.max(margin, top);
    setPos({ top, left, arrowSide });
    // Only reposition when the anchor changes, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRect]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onPointerDown = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  const applyDuration = useCallback(
    (minutes: number) => {
      const s = combine(dateStr, startTime);
      if (!s) return;
      const e = new Date(s.getTime() + minutes * 60000);
      setEndTime(format(e, 'HH:mm'));
    },
    [dateStr, startTime],
  );

  const submit = async () => {
    if (!timesValid || !start || !end || !title.trim() || saving) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await onSave({
        title: title.trim(),
        start,
        end,
        location: location.trim(),
        description: description.trim(),
      });
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Could not update event');
      setSaving(false);
    }
  };

  const remove = async () => {
    if (deleting) return;
    setDeleting(true);
    setErrorMsg(null);
    try {
      await onDelete();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Could not delete');
    } finally {
      // Also resets when the user cancels the confirm dialog (popover stays open).
      setDeleting(false);
    }
  };

  const anchorMidY = anchorRect.top + Math.min(anchorRect.height, 28) / 2;
  const arrowTop = pos ? Math.min(Math.max(anchorMidY - pos.top - 6, 12), 320) : 20;

  return (
    <div
      ref={cardRef}
      className="eac-event-popover"
      style={{
        top: pos?.top ?? anchorRect.top,
        left: pos?.left ?? anchorRect.right + 10,
        width: POPOVER_WIDTH,
        visibility: pos ? 'visible' : 'hidden',
      }}
      role="dialog"
      aria-modal
      aria-label={isGrouped ? 'Edit trip event' : 'Edit event'}
    >
      {pos && (
        <span
          className={`eac-event-popover-arrow is-${pos.arrowSide}`}
          style={{ top: arrowTop }}
          aria-hidden
        />
      )}

      <div className="eac-event-popover-head">
        <span className="eac-event-popover-cal">
          <span className="eac-event-popover-swatch" style={{ background: calendarColor }} />
          {calendarName}
        </span>
        <span className="eac-event-popover-head-actions">
          {event.htmlLink && (
            <a
              href={event.htmlLink}
              target="_blank"
              rel="noreferrer"
              title="Open in Google Calendar"
            >
              <ExternalLink size={14} aria-hidden />
            </a>
          )}
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={15} aria-hidden />
          </button>
        </span>
      </div>

      {isGrouped && (
        <p className="eac-event-popover-trip-note">
          Part of a linked trip — changing times updates related travel blocks.
        </p>
      )}

      <div className="eac-field">
        <label>Title</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
      </div>

      <div className="eac-field">
        <label>Date</label>
        <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)} />
      </div>

      <div className="eac-event-popover-times">
        <div className="eac-field">
          <label>Start</label>
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
        </div>
        <div className="eac-field">
          <label>End</label>
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
        </div>
        <span className={`eac-event-popover-duration${timesValid ? '' : ' is-invalid'}`}>
          {timesValid ? formatDuration(durationMin!) : 'End must be after start'}
        </span>
      </div>

      <div className="eac-event-popover-chips">
        {DURATION_CHIPS.map(chip => (
          <button
            key={chip.label}
            type="button"
            className={durationMin === chip.minutes ? 'is-active' : ''}
            onClick={() => applyDuration(chip.minutes)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div className="eac-field">
        <label>Location</label>
        <input
          value={location}
          onChange={e => setLocation(e.target.value)}
          placeholder="Add location"
        />
      </div>

      <div className="eac-field">
        <label>Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={3}
          placeholder="Add notes"
        />
      </div>

      {errorMsg && <p className="eac-event-popover-error">{errorMsg}</p>}

      <div className="eac-modal-actions">
        <button type="button" className="danger" onClick={() => void remove()} disabled={deleting}>
          {deleting ? 'Deleting…' : isGrouped ? 'Delete trip' : 'Delete'}
        </button>
        <button type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => void submit()}
          disabled={saving || !timesValid || !title.trim()}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default EventEditorPopover;
